/**
 * Audio processing utilities for Web Audio API
 */

/**
 * Downsample audio buffer from source rate to target rate
 */
export function downsampleBuffer(buffer: Float32Array, sourceRate: number, targetRate: number): Float32Array {
  if (sourceRate === targetRate) {
    return buffer;
  }
  
  const ratio = sourceRate / targetRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);
  
  for (let i = 0; i < newLength; i++) {
    const sourceIndex = i * ratio;
    const index = Math.floor(sourceIndex);
    const fraction = sourceIndex - index;
    
    if (index + 1 < buffer.length) {
      // Linear interpolation
      result[i] = buffer[index] * (1 - fraction) + buffer[index + 1] * fraction;
    } else {
      result[i] = buffer[index] || 0;
    }
  }
  
  return result;
}

/**
 * Convert Float32 audio samples to PCM16 (Int16Array)
 */
export function floatTo16BitPCM(float32Array: Float32Array): Int16Array {
  const pcm16 = new Int16Array(float32Array.length);
  
  for (let i = 0; i < float32Array.length; i++) {
    // Clamp to [-1, 1] and convert to 16-bit signed integer
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
  }
  
  return pcm16;
}

/**
 * Convert ArrayBuffer to base64 string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export class AudioProcessor {
  private audioContext: AudioContext | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private isPlaying: boolean = false;
  private audioQueue: string[] = [];
  private isProcessingQueue: boolean = false;

  async initializeAudioContext(): Promise<AudioContext> {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    
    return this.audioContext;
  }

  async requestMicrophoneAccess(): Promise<MediaStream> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000, // Match Azure VoiceLive expected sample rate
          channelCount: 1,   // Mono audio
          echoCancellation: true,  // ✅ ENABLED - Safari/iOS requires browser-level AEC
          noiseSuppression: true,  // ✅ ENABLED - Helps prevent echo on Safari/iOS
          autoGainControl: true    // ✅ ENABLED - Volume normalization
        }
      });
      return this.stream;
    } catch (error) {
      console.error('Error requesting microphone access:', error);
      throw new Error(`Microphone access denied: ${(error as Error).message}`);
    }
  }

  setupAudioAnalyzer(stream: MediaStream): AnalyserNode {
    if (!this.audioContext) {
      throw new Error('Audio context not initialized');
    }

    const source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.8;

    source.connect(this.analyser);
    
    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    return this.analyser;
  }

  getAudioLevel(): number {
    if (!this.analyser || !this.dataArray) {
      return 0;
    }

    // Create a new Uint8Array to satisfy TypeScript typing
    const audioData = new Uint8Array(this.dataArray.length);
    this.analyser.getByteFrequencyData(audioData);
    
    let sum = 0;
    for (let i = 0; i < audioData.length; i++) {
      sum += audioData[i];
    }
    
    const average = sum / audioData.length;
    return Math.min(Math.max(average / 128, 0), 1); // Normalize to 0-1
  }

  async startRecording(
    onDataAvailable: (audioData: string) => void,
    onPronunciationFrame?: (pcm16Data: ArrayBuffer) => void
  ): Promise<void> {
    console.log('🎤 [AudioProcessor.startRecording] Method called');
    console.log(`🎤 [AudioProcessor.startRecording] onPronunciationFrame: ${onPronunciationFrame ? 'DEFINED ✅' : 'UNDEFINED ❌'}`);
    
    if (!this.stream) {
      throw new Error('No audio stream available');
    }

    if (!this.audioContext) {
      throw new Error('Audio context not initialized');
    }

    this.recordedChunks = [];

    // Use Web Audio API for real-time PCM capture instead of MediaRecorder
    const source = this.audioContext.createMediaStreamSource(this.stream);
    const processor = this.audioContext.createScriptProcessor(4096, 1, 1); // 4096 samples, mono in/out
    
    processor.onaudioprocess = (event) => {
      const inputBuffer = event.inputBuffer;
      const inputData = inputBuffer.getChannelData(0); // Get mono channel
      
      // Path 1: VoiceLive (24kHz, base64-encoded)
      const voiceLive24k = downsampleBuffer(inputData, this.audioContext!.sampleRate, 24000);
      const pcmData24k = floatTo16BitPCM(voiceLive24k);
      const base64Data = arrayBufferToBase64(pcmData24k.buffer as ArrayBuffer);
      onDataAvailable(base64Data);
      
      // Path 2: Pronunciation (16kHz, binary) - if enabled
      if (onPronunciationFrame) {
        const pronunciation16k = downsampleBuffer(inputData, this.audioContext!.sampleRate, 16000);
        const pcmData16k = floatTo16BitPCM(pronunciation16k);
        if (pcmData16k.buffer.byteLength > 10000) {
          console.log(`🎤 [AudioUtils] Sending pronunciation frame: ${pcmData16k.buffer.byteLength} bytes`);
        }
        onPronunciationFrame(pcmData16k.buffer as ArrayBuffer);
      } else {
        console.warn('⚠️ [AudioUtils] onPronunciationFrame callback is undefined - audio not sent to pronunciation WebSocket');
      }
    };

    // Connect the audio pipeline
    source.connect(processor);
    processor.connect(this.audioContext.destination);

    // Store reference to processor for cleanup
    (this as any).audioProcessor = processor;
  }

  stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // Clean up audio processor if it exists
      const processor = (this as any).audioProcessor;
      if (processor) {
        processor.disconnect();
        (this as any).audioProcessor = null;
      }

      if (this.mediaRecorder) {
        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.recordedChunks, { 
            type: this.mediaRecorder?.mimeType || 'audio/webm' 
          });
          resolve(audioBlob);
        };
        this.mediaRecorder.stop();
      } else {
        // If using Web Audio API without MediaRecorder, return empty blob
        resolve(new Blob([], { type: 'audio/pcm' }));
      }
    });
  }

  private convertFloat32ToPCM16(float32Array: Float32Array): Int16Array {
    // Target sample rate is 24kHz - resample if needed
    const targetSampleRate = 24000;
    const currentSampleRate = this.audioContext?.sampleRate || 48000;
    
    let resampledData: Float32Array;
    if (currentSampleRate !== targetSampleRate) {
      resampledData = this.resampleAudio(float32Array, currentSampleRate, targetSampleRate);
    } else {
      resampledData = float32Array;
    }
    
    // Convert float32 samples to int16
    const pcmData = new Int16Array(resampledData.length);
    for (let i = 0; i < resampledData.length; i++) {
      // Clamp to [-1, 1] and convert to 16-bit signed integer
      const sample = Math.max(-1, Math.min(1, resampledData[i]));
      pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    
    // Ensure even number of bytes by padding with a zero if needed
    if (pcmData.byteLength % 2 !== 0) {
      const paddedData = new Int16Array(pcmData.length + 1);
      paddedData.set(pcmData);
      paddedData[pcmData.length] = 0;
      return paddedData;
    }
    
    return pcmData;
  }

  private resampleAudio(audioData: Float32Array, sourceRate: number, targetRate: number): Float32Array {
    if (sourceRate === targetRate) {
      return audioData;
    }
    
    const ratio = sourceRate / targetRate;
    const newLength = Math.round(audioData.length / ratio);
    const resampled = new Float32Array(newLength);
    
    for (let i = 0; i < newLength; i++) {
      const sourceIndex = i * ratio;
      const index = Math.floor(sourceIndex);
      const fraction = sourceIndex - index;
      
      if (index + 1 < audioData.length) {
        // Linear interpolation
        resampled[i] = audioData[index] * (1 - fraction) + audioData[index + 1] * fraction;
      } else {
        resampled[i] = audioData[index] || 0;
      }
    }
    
    return resampled;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  async playAudioFromBase64(base64Data: string): Promise<void> {
    if (!this.audioContext) {
      throw new Error('Audio context not initialized');
    }

    // Skip if empty data
    if (!base64Data || base64Data.length === 0) {
      return;
    }

    // Add to queue for sequential playback
    this.audioQueue.push(base64Data);
    
    // Process queue if not already processing
    if (!this.isProcessingQueue) {
      await this.processAudioQueue();
    }
  }

  private async processAudioQueue(): Promise<void> {
    if (this.isProcessingQueue || this.audioQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    try {
      while (this.audioQueue.length > 0) {
        const base64Data = this.audioQueue.shift()!;
        await this.playAudioChunkDirectly(base64Data);
        
        // Small delay to prevent audio overlap
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    } catch (error) {
      console.error('Error processing audio queue:', error);
    } finally {
      this.isProcessingQueue = false;
    }
  }

  private async playAudioChunkDirectly(base64Data: string): Promise<void> {
    if (!this.audioContext) {
      throw new Error('Audio context not initialized');
    }

    try {
      // Convert base64 to array buffer
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Skip if no actual audio data
      if (bytes.length === 0) {
        return;
      }

      // Convert PCM16 bytes to Float32 for Web Audio API
      const pcm16Data = new Int16Array(bytes.buffer);
      const sampleRate = 24000; // Azure VoiceLive uses 24kHz
      const audioBuffer = this.audioContext.createBuffer(1, pcm16Data.length, sampleRate);
      const channelData = audioBuffer.getChannelData(0);
      
      // Convert Int16 PCM to Float32 (-1.0 to 1.0 range)
      for (let i = 0; i < pcm16Data.length; i++) {
        channelData[i] = pcm16Data[i] / 32768.0; // Convert from int16 range to float32 range
      }

      // Create and play audio source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      
      // Track playback state
      this.isPlaying = true;
      
      // Return promise that resolves when audio finishes
      return new Promise<void>((resolve) => {
        source.onended = () => {
          this.isPlaying = false;
          resolve();
        };
        
        source.start();
      });
    } catch (error) {
      this.isPlaying = false;
      console.error('Error playing PCM16 audio chunk:', error);
      throw new Error(`Audio chunk playback failed: ${(error as Error).message}`);
    }
  }

  isCurrentlyPlaying(): boolean {
    return this.isPlaying;
  }

  cleanup(): void {
    // Clean up audio processor
    const processor = (this as any).audioProcessor;
    if (processor) {
      processor.disconnect();
      (this as any).audioProcessor = null;
    }

    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
    this.dataArray = null;
    this.recordedChunks = [];
    this.isPlaying = false;
    this.audioQueue = [];
    this.isProcessingQueue = false;
  }

  // Method to clear audio queue without stopping current playback
  clearAudioQueue(): void {
    this.audioQueue = [];
  }

  static async checkAudioSupport(): Promise<boolean> {
    return !!(navigator.mediaDevices && 
             typeof navigator.mediaDevices.getUserMedia === 'function' && 
             typeof window.AudioContext !== 'undefined' &&
             typeof window.MediaRecorder !== 'undefined');
  }

  static getAudioErrorMessage(error: Error): string {
    const message = error.message.toLowerCase();
    
    if (message.includes('permission') || message.includes('denied')) {
      return 'Microphone permission denied. Please allow microphone access and try again.';
    }
    
    if (message.includes('not found') || message.includes('no audio')) {
      return 'No microphone found. Please connect a microphone and try again.';
    }
    
    if (message.includes('constraint') || message.includes('format')) {
      return 'Audio format not supported. Please try with a different browser.';
    }
    
    return 'Audio system error. Please check your audio settings and try again.';
  }

  isAudioContextInitialized(): boolean {
    return this.audioContext !== null;
  }
}

// Simplified interface for the MicButton component
export interface AudioRecorder {
  start(): void;
  stop(): void;
}

export interface AudioRecorderOptions {
  onDataAvailable: (base64Data: string) => void;
  onPronunciationFrame?: (pcm16Data: ArrayBuffer) => void; // Optional: for pronunciation WebSocket
  onStop: () => void;
}

export async function createAudioRecorder(options: AudioRecorderOptions): Promise<AudioRecorder> {
  console.log('🔧 [createAudioRecorder] Called with options:');
  console.log(`🔧 [createAudioRecorder] onDataAvailable: ${typeof options.onDataAvailable}`);
  console.log(`🔧 [createAudioRecorder] onPronunciationFrame: ${options.onPronunciationFrame ? 'DEFINED ✅' : 'UNDEFINED ❌'} (type: ${typeof options.onPronunciationFrame})`);
  console.log(`🔧 [createAudioRecorder] onStop: ${typeof options.onStop}`);
  
  const processor = new AudioProcessor();
  
  try {
    await processor.initializeAudioContext();
    const stream = await processor.requestMicrophoneAccess();
    processor.setupAudioAnalyzer(stream);
    
    return {
      start: () => {
        console.log(`🔧 [AudioRecorder.start] About to call startRecording with onPronunciationFrame: ${options.onPronunciationFrame ? 'DEFINED ✅' : 'UNDEFINED ❌'}`);
        processor.startRecording(options.onDataAvailable, options.onPronunciationFrame);
      },
      stop: () => {
        processor.stopRecording().then(() => {
          options.onStop();
        });
      }
    };
  } catch (error) {
    processor.cleanup();
    throw error;
  }
}